import { computed, readonly, ref } from 'vue'
import {
  APP_VERSION_TOPIC,
  type AppVersionPayload
} from '§A/websocket/app-version.type'
import { useKeycloak } from '@/shared/composables/useKeycloak'
import { useWS } from '@/shared/composables/useWS'

// Detects when a new app version has been deployed by comparing the backend's
// reported version (pushed over WS on subscribe / reconnect — see
// app-version.type.ts) against the version this page was built with. On a
// mismatch it flips `updateAvailable`, which AppVersionReloadDialog watches.
//
// Singleton module state so a single subscription is shared no matter how many
// callers invoke the composable.
const currentVersion = __APP_VERSION__
const latestVersion = ref<string>(currentVersion)
const updateAvailable = ref(false)
let unsubscribe: (() => void) | null = null

export const useAppVersion = () => {
  const { subscribe } = useWS()
  const { flushAll } = useKeycloak()

  // Opens the WS subscription that watches for version changes. Idempotent —
  // safe to call from the always-mounted dialog's setup.
  const init = (): void => {
    if (unsubscribe) return
    unsubscribe = subscribe<AppVersionPayload>(APP_VERSION_TOPIC, (payload) => {
      if (!payload || typeof payload.version !== 'string') return
      latestVersion.value = payload.version
      // The backend version differing from the bundle we booted with means a
      // new version is live. Latch it on; never flip back off, so a flapping
      // backend can't dismiss a pending reload.
      if (payload.version !== currentVersion) updateAvailable.value = true
    })
  }

  const stop = (): void => {
    unsubscribe?.()
    unsubscribe = null
  }

  // Persist any unsaved work, then hard-reload to pull the new frontend bundle.
  // Reuses the same flush-handler registry that saves before logout
  // (useKeycloak / ConceptAutoSaver), so in-store edits are written back first.
  // Reload happens even if a flush handler rejects — a failed save must not
  // strand the user on a stale build.
  const saveAllAndCloseDialogAndReloadPage = async (): Promise<void> => {
    updateAvailable.value = false
    try {
      await flushAll()
    }
    catch (err) {
      console.error('[useAppVersion] flush before reload failed', err)
    }
    finally {
      window.location.reload()
    }
  }

  return {
    currentVersion,
    latestVersion: readonly(latestVersion),
    updateAvailable: computed(() => updateAvailable.value),
    init,
    stop,
    saveAllAndCloseDialogAndReloadPage
  }
}
